import {
  faChevronDown,
  faChevronUp,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { forwardRef, MouseEvent } from 'react';

type Props = {
  /** Placeholder text shown when no value is applied (e.g. "Client"). */
  placeholder: string;
  /** Active selection label. When set, the pill renders as a green chip. */
  chipLabel?: string;
  /** Optional "+N" indicator next to the chip label for multi-selects. */
  chipMore?: string;
  open: boolean;
  onToggle: () => void;
  /** Called when the user clicks × on the chip. Omit to hide the X. */
  onClear?: () => void;
  id?: string;
  /** A11y label for the chevron toggle button. */
  a11yLabel: string;
};

const FilterPill = forwardRef<HTMLButtonElement, Props>(
  (
    {
      placeholder,
      chipLabel,
      chipMore,
      open,
      onToggle,
      onClear,
      id,
      a11yLabel,
    },
    ref,
  ) => {
    const hasValue = Boolean(chipLabel);

    const handleClear = (e: MouseEvent) => {
      e.stopPropagation();
      onClear?.();
    };

    return (
      <button
        ref={ref}
        type="button"
        id={id}
        className={classNames('filter-pill', { 'is-placeholder': !hasValue })}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={a11yLabel}
        onClick={onToggle}
      >
        {hasValue ? (
          <span className="filter-chip">
            <span className="filter-chip-label">{chipLabel}</span>
            {chipMore ? (
              <span className="filter-chip-more">{chipMore}</span>
            ) : null}
            {onClear ? (
              <span
                role="button"
                tabIndex={0}
                aria-label="Clear filter"
                className="chip-x"
                onClick={handleClear}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    e.preventDefault();
                    onClear?.();
                  }
                }}
              >
                <FontAwesomeIcon icon={faTimes} fixedWidth />
              </span>
            ) : null}
          </span>
        ) : (
          <span className="chip-plain">{placeholder}</span>
        )}
        <span className="filter-pill-chevron" aria-hidden>
          <FontAwesomeIcon
            icon={open ? faChevronUp : faChevronDown}
            fixedWidth
          />
        </span>
      </button>
    );
  },
);

FilterPill.displayName = 'FilterPill';

export default FilterPill;
